import React from 'react';
import { Sparkles, Image as ImageIcon } from 'lucide-react';
import { SheetImage } from './types';

interface PropertySheetGalleryProps {
  images: SheetImage[];
  badgeText?: string;
}

function getSafeImageUrl(url?: string): string {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('/')) return url;
  return `/api/image-proxy?url=${encodeURIComponent(url)}`;
}

const ImageCard: React.FC<{ url: string; alt: string; aspect?: string; badge?: string }> = ({
  url,
  alt,
  aspect = 'aspect-[4/3]',
  badge,
}) => (
  <div className={`relative ${aspect} rounded-2xl overflow-hidden bg-slate-100 shadow-2xs border border-slate-200/80`}>
    <img
      src={getSafeImageUrl(url)}
      alt={alt}
      className="w-full h-full object-cover"
      crossOrigin="anonymous"
      loading="eager"
    />
    {badge && (
      <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#0a2540]/90 backdrop-blur-md text-[#f59e0b] border border-white/20 text-[10px] font-extrabold uppercase tracking-wider shadow-sm z-10">
        <Sparkles className="w-3 h-3 text-[#f59e0b]" />
        <span className="text-white">{badge}</span>
      </div>
    )}
  </div>
);

export const PropertySheetGallery: React.FC<PropertySheetGalleryProps> = ({ images, badgeText }) => {
  const list = images.filter((img) => Boolean(img?.url));

  if (list.length === 0) {
    return (
      <div className="w-full h-36 rounded-2xl bg-slate-50 border border-slate-200/80 flex flex-col items-center justify-center text-slate-400 gap-1.5 mb-3.5 shadow-2xs">
        <ImageIcon className="w-6 h-6 text-slate-300" />
        <span className="text-xs font-semibold text-slate-500">Fotografía en proceso de actualización</span>
      </div>
    );
  }

  if (list.length === 1) {
    return (
      <div className="w-full mb-3.5">
        <ImageCard url={list[0].url} alt="Foto principal" aspect="aspect-[2.4/1]" badge={badgeText} />
      </div>
    );
  }

  if (list.length === 2) {
    return (
      <div className="w-full grid grid-cols-2 gap-2.5 mb-3.5">
        <ImageCard url={list[0].url} alt="Foto principal" aspect="aspect-[16/10]" badge={badgeText} />
        <ImageCard url={list[1].url} alt="Foto secundaria" aspect="aspect-[16/10]" />
      </div>
    );
  }

  if (list.length === 3) {
    return (
      <div className="w-full grid grid-cols-3 gap-2.5 mb-3.5">
        <ImageCard url={list[0].url} alt="Foto principal" badge={badgeText} />
        <ImageCard url={list[1].url} alt="Foto secundaria" />
        <ImageCard url={list[2].url} alt="Foto adicional" />
      </div>
    );
  }

  // 4 o más fotos: Cuadrícula simétrica 2x2 (mosaico balanceado)
  return (
    <div className="w-full grid grid-cols-2 gap-2.5 mb-3.5">
      <ImageCard url={list[0].url} alt="Foto principal" aspect="aspect-[16/10]" badge={badgeText} />
      <ImageCard url={list[1].url} alt="Foto 2" aspect="aspect-[16/10]" />
      <ImageCard url={list[2].url} alt="Foto 3" aspect="aspect-[16/10]" />
      <ImageCard url={list[3].url} alt="Foto 4" aspect="aspect-[16/10]" />
    </div>
  );
};
